<template>
  <div class="rd-page">
    <container>
      <div class="pg-title-row">
        <div>
          <h1 class="pg-title">Runner Data</h1>
          <p class="pg-title-sub">Review runner profile, private notes, best records, tags and past sessions</p>
        </div>
        <router-link
          :to="{ name: 'runner-data', params: { runnerId: this.$props.runnerId }, query: { runmetrix: 1 } }"
          target="_blank"
          class="rd-runmetrix-link"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 3v18h18"/>
            <path d="M7 15l4-4 3 3 5-7"/>
          </svg>
          Runmetrix
        </router-link>
      </div>

      <div v-if="runnerLoading" class="rd-dashboard">
        <div class="rd-card rd-profile-card">
          <div class="rd-profile-loading sk-shimmer"></div>
          <div class="rd-sk-line rd-sk-line--wide sk-shimmer"></div>
          <div class="rd-sk-line sk-shimmer"></div>
          <div class="rd-sk-line sk-shimmer"></div>
        </div>
        <div class="rd-card rd-detail-card">
          <div v-for="n in 10" :key="'detail-sk-' + n" class="rd-sk-row">
            <div class="rd-sk-label sk-shimmer"></div>
            <div class="rd-sk-value sk-shimmer"></div>
          </div>
        </div>
        <div class="rd-card rd-best-card">
          <div class="rd-sk-line rd-sk-line--wide sk-shimmer"></div>
          <div v-for="n in 4" :key="'best-sk-' + n" class="rd-best-skeleton sk-shimmer"></div>
        </div>
      </div>

      <div v-else-if="$route.query.runmetrix == 1">
        <run-metrix :runmetrix="runmetrixData" :runnerId="this.$props.runnerId"></run-metrix>
      </div>

      <div v-else class="rd-content">
        <div class="rd-dashboard">
          <section class="rd-card rd-profile-card">
            <div class="rd-card-header">
              <span class="rd-icon-pill">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21a8 8 0 0 0-16 0"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <div>
                <h2 class="rd-card-title">Profile</h2>
                <p class="rd-card-sub">Runner identity and confidential account data</p>
              </div>
            </div>

            <div class="rd-profile-top">
              <div class="rd-profile-image-container">
                <img v-if="runner.imageUrl" :src="runner.imageUrl" alt="Profile" class="rd-profile-image" />
                <div v-else class="rd-profile-placeholder">
                  <svg class="rd-profile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
              </div>
              <div>
                <div class="rd-runner-name">{{ runner.runner ? runner.runner.firstName : '-' }}</div>
                <div class="rd-runner-id">ID {{ runner.runner ? runner.runner.id : runnerId }}</div>
              </div>
            </div>

            <div class="rd-confidential">
              <div class="rd-confidential-label">Confidential</div>
              <div class="rd-private-row">
                <span>Mail address</span>
                <strong>{{ runner.runner && runner.runner.user ? runner.runner.user.email : '-' }}</strong>
              </div>
              <div class="rd-private-row">
                <span>Height</span>
                <strong>{{ runner.runner ? runner.runner.height : '-' }}</strong>
              </div>
              <div class="rd-private-row">
                <span>Weight</span>
                <strong>{{ runner.runner ? runner.runner.weight : '-' }}</strong>
              </div>
              <button class="rd-ghost-btn" type="button" @click.stop.prevent="openEmailEditModal()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 4h16v16H4z"/>
                  <path d="M22 6l-10 7L2 6"/>
                </svg>
                Edit Email
              </button>
            </div>

            <div v-if="runner.runner" class="rd-edit-block">
              <div class="rd-block-label">Catchphrase</div>
              <div v-if="!editingCatchPhrase" class="rd-field-display">
                <span class="rd-field-value">{{ runner.runner.catchPhrase || 'No catchphrase set' }}</span>
                <button class="rd-icon-btn" type="button" title="Edit catchphrase" @click="startEditCatchPhrase()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
              <div v-else class="rd-field-edit">
                <textarea v-model="runner.runner.catchPhrase" maxlength="255" name="catchPhrase" placeholder="Enter catchphrase" class="rd-textarea rd-textarea--small"></textarea>
                <button class="rd-icon-btn rd-icon-btn--save" type="button" title="Save catchphrase" @click="saveCatchPhrase()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>
              </div>
            </div>
            <edit-email :runner="runner" :show="showEmailModal" @saved="onSavedEmail" @closed="onClosed"></edit-email>
          </section>

          <section class="rd-card rd-detail-card">
            <div class="rd-card-header">
              <span class="rd-icon-pill">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M8 6h13"/>
                  <path d="M8 12h13"/>
                  <path d="M8 18h13"/>
                  <path d="M3 6h.01"/>
                  <path d="M3 12h.01"/>
                  <path d="M3 18h.01"/>
                </svg>
              </span>
              <div>
                <h2 class="rd-card-title">Runner Summary</h2>
                <p class="rd-card-sub">Core metadata and activity counters</p>
              </div>
            </div>

            <div class="rd-detail-grid">
              <div class="rd-detail-item"><span>Gender</span><strong>{{ runner.runner ? runner.runner.gender : '-' }}</strong></div>
              <div class="rd-detail-item"><span>Location</span><strong>{{ runner.runner ? runner.runner.lastName : '-' }}</strong></div>
              <div class="rd-detail-item rd-detail-item--wide">
                <span>URL</span>
                <a v-if="runner.runner && runner.runner.website" :href="runner.runner.website" target="_blank" class="rd-link">{{ runner.runner.website }}</a>
                <strong v-else>-</strong>
              </div>
              <div class="rd-detail-item"><span>Total km</span><strong>{{ runner.totalDistance || '0 Km' }}</strong></div>
              <div class="rd-detail-item"><span>Last Run</span><strong>-</strong></div>
              <div class="rd-detail-item"><span>Days since last Run</span><strong>{{ runner.daysSinceLastClass }}</strong></div>
              <div class="rd-detail-item"><span>Last 30 days Best Rank</span><strong>{{ runner.bestPlaceCurrentMonth }}</strong></div>
              <div class="rd-detail-item"><span>Completed sessions</span><strong>{{ runner.totalRuns }}</strong></div>
              <div class="rd-detail-item"><span>Account created</span><strong>{{ createdOn || '-' }}</strong></div>
            </div>

            <div class="rd-medal-row">
              <span class="rd-medal-pill">Gold {{ Gold }}</span>
              <span class="rd-medal-pill">Silver {{ Silver }}</span>
              <span class="rd-medal-pill">Bronze {{ Bronze }}</span>
            </div>

            <div class="rd-activity-grid">
              <div><span>ラン・ウォーク</span><strong>{{ runner.totalRuns }}</strong></div>
              <div><span>筋トレ</span><strong>{{ runner.totalCountIndoorTraining }}</strong></div>
              <div><span>ストレッチ</span><strong>{{ runner.totalCountStretch }}</strong></div>
              <div><span>ヨガ</span><strong>{{ runner.totalCountYoga }}</strong></div>
            </div>

            <div class="rd-edit-block">
              <div class="rd-block-label">Profile</div>
              <div v-if="!editingProfileText" class="rd-field-display">
                <span class="rd-field-value">{{ profileTexts || 'No profile text set' }}</span>
                <button class="rd-icon-btn" type="button" title="Edit profile text" @click="startEditProfileText()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
              <div v-else class="rd-field-edit">
                <textarea v-model="profileTextEdit" maxlength="500" placeholder="Enter profile text" class="rd-textarea"></textarea>
                <button class="rd-icon-btn rd-icon-btn--save" type="button" title="Save profile text" @click="saveProfileText()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>
              </div>
            </div>
          </section>

          <section class="rd-card rd-best-card">
            <div class="rd-card-header">
              <span class="rd-icon-pill">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 3v18h18"/>
                  <path d="M7 14l3-3 4 4 5-8"/>
                </svg>
              </span>
              <div>
                <h2 class="rd-card-title">Best Records</h2>
                <p class="rd-card-sub">Current year compared with all-time performance</p>
              </div>
            </div>

            <div class="rd-best-table">
              <div class="rd-best-head">
                <span>Duration</span>
                <span>Best {{ currentYear }}</span>
                <span>All time</span>
              </div>
              <div v-for="duration in [20, 30, 40, 60]" :key="'duration-' + duration" class="rd-best-row">
                <div class="rd-best-duration">{{ duration }} min</div>
                <div class="rd-best-cell">
                  <div v-if="currentYearbest[duration] && currentYearbest[duration].length">
                    <div v-for="bestRecord in currentYearbest[duration]" :key="'year-' + duration + '-' + bestRecord.id" class="rd-record-line">
                      <span>{{ date(convertToLocal(bestRecord.raceResume.finishDate)) }}</span>
                      <strong>{{ (bestRecord.distance / 1000).toFixed(2) }} km</strong>
                    </div>
                  </div>
                  <span v-else class="rd-muted">-</span>
                </div>
                <div class="rd-best-cell rd-best-cell--highlight">
                  <div v-if="best[duration] && best[duration].length">
                    <div v-for="bestRecord in best[duration]" :key="'all-' + duration + '-' + bestRecord.id" class="rd-record-line">
                      <span>{{ date(convertToLocal(bestRecord.raceResume.finishDate)) }}</span>
                      <strong>{{ (bestRecord.distance / 1000).toFixed(2) }} km</strong>
                    </div>
                  </div>
                  <span v-else class="rd-muted">-</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section class="rd-card rd-notes-card">
          <div class="rd-card-header">
            <span class="rd-icon-pill">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <path d="M14 2v6h6"/>
                <path d="M16 13H8"/>
                <path d="M16 17H8"/>
              </svg>
            </span>
            <div>
              <h2 class="rd-card-title">Notes & Tags</h2>
              <p class="rd-card-sub">Internal memo fields and runner segmentation controls</p>
            </div>
          </div>

          <div class="rd-notes-grid">
            <div class="rd-edit-block">
              <div class="rd-block-label">Memo</div>
              <div v-if="!editingMemo" class="rd-field-display">
                <span class="rd-field-value">{{ memo || 'No memo set' }}</span>
                <button class="rd-icon-btn" type="button" title="Edit memo" @click="startEditMemo()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
              <div v-else class="rd-field-edit">
                <textarea v-model="memo" maxlength="255" name="memo" placeholder="Enter memo" class="rd-textarea rd-textarea--small"></textarea>
                <button class="rd-icon-btn rd-icon-btn--save" type="button" title="Save memo" @click="saveMemo()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="rd-edit-block">
              <div class="rd-block-label">CM Memo</div>
              <div v-if="!editingCMMemo" class="rd-field-display">
                <span class="rd-field-value">{{ cmmemo || 'No CM memo set' }}</span>
                <button class="rd-icon-btn" type="button" title="Edit CM memo" @click="startEditCMMemo()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
              <div v-else class="rd-field-edit">
                <textarea v-model="cmmemo" maxlength="2000" name="cmmemo" placeholder="Enter CM memo" class="rd-textarea rd-textarea--small"></textarea>
                <button class="rd-icon-btn rd-icon-btn--save" type="button" title="Save CM memo" @click="saveCMMemo()">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div class="rd-tag-grid">
            <div class="rd-tag-field runner-tags">
              <p class="rd-tag-label">Tags</p>
              <selectize
                v-bind:options="options"
                v-model="selected"
                multiple
                size="1"
                :runnerid="this.$props.runnerId"
                :isRunnerTag="true"
                data-dropdown-parent="body"
                data-max-options="100"
                data-value-field="id"
                data-label-field="name"
                data-search-field="name"
                data-sort-field="name">
              </selectize>
            </div>
            <div class="rd-tag-field promocode-tags">
              <p class="rd-tag-label">Promo Code Tags</p>
              <selectize
                v-bind:options="options"
                v-model="selectedPromoTags"
                multiple
                size="1"
                :runnerid="this.$props.runnerId"
                :isPromoCodeTag="true"
                data-dropdown-parent="body"
                data-max-options="100"
                data-value-field="id"
                data-label-field="name"
                data-search-field="name"
                data-sort-field="name">
              </selectize>
            </div>
          </div>
        </section>

        <section class="rd-card rd-runs-card">
          <div class="rd-card-header rd-card-header--split">
            <div class="rd-card-heading">
              <span class="rd-icon-pill">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M8 6h13"/>
                  <path d="M8 12h13"/>
                  <path d="M8 18h13"/>
                  <path d="M3 6h.01"/>
                  <path d="M3 12h.01"/>
                  <path d="M3 18h.01"/>
                </svg>
              </span>
              <div>
                <h2 class="rd-card-title">履歴 Past Runs</h2>
                <p class="rd-card-sub">Session history, distances, route links and post-run responses</p>
              </div>
            </div>
            <div class="rd-actions">
              <button class="rd-ghost-btn" type="button" @click="openAssignPromoCodeModal()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
                Assign Promo Code
              </button>
              <button class="rd-primary-btn" type="button" @click="addRace()">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add to Race
              </button>
            </div>
          </div>

          <div v-if="isLoading" class="rd-table-skeleton">
            <div v-for="n in 5" :key="'run-sk-' + n" class="rd-table-sk-row sk-shimmer"></div>
          </div>
          <div v-else class="rd-table-wrap">
            <v-table
              is-horizontal-resize
              :vertical-resize-offset="60"
              :multiple-sort="false"
              :is-loading="isLoading"
              even-bg-color="#f8f8fb"
              :column-cell-class-name="columnCellClass"
              :columns="tableConfig.columns"
              :table-data="tableConfig.tableData"
              :paging-index="(pageIndex - 1) * pageSize"
              @on-custom-comp="customCompFunc">
            </v-table>
          </div>
          <div class="rd-pagination">
            <v-pagination @page-change="pageChange" @page-size-change="pageSizeChange" :total="pageTotal" :page-size="pageSize" :layout="['total', 'prev', 'pager', 'next', 'sizer', 'jumper']"></v-pagination>
          </div>

          <edit-distance :race="raceData" :show="showModal" @saved="onSaved" @closed="onClosed"></edit-distance>
          <add-race-data :race="raceData" :runnerId="this.$props.runnerId" :show="showAddRaceModal" @saved="onSavedRace" @closed="onClosed"></add-race-data>
          <assign-promo-code :runnerId="this.$props.runnerId" :show="showAssignPromoCodeModal" @saved="onSavedPromoCode" @closed="onClosed"></assign-promo-code>
        </section>
      </div>
    </container>
  </div>
</template>

<script>
let dialogOptions = {
    html: false, // set to true if your message contains HTML tags. eg: "Delete <b>Foo</b> ?"
    loader: false, // set to true if you want the dailog to show a loader after click on "proceed"
    reverse: false, // switch the button positions (left to right, and vise versa)
    cancelText: 'No',
    okText: 'Yes',
    animation: 'zoom', // Available: "zoom", "bounce", "fade"
    type: 'basic', // coming soon: 'soft', 'hard'
    verification: 'continue', // for hard confirm, user will be prompted to type this to enable the proceed button
    clicksCount: 3,
    animation: 'bounce', // for soft confirm, user will be asked to click on "proceed" btn 3 times before actually proceeding
  }


  import Container from '@/components/Container'
  import EditDistance from './EditDistance'
  import EditEmail from './EditEmail'
  import Selectize from '@/components/Selectize'
  import AddRaceData from './AddRaceData'
  import AssignPromoCode from './AssignPromoCode'
  import RunMetrix from './RunMetrix'
  import 'vue-easytable/libs/themes-base/index.css'
  import {VTable,VPagination} from 'vue-easytable'
  import VuejsDialog from "vuejs-dialog"
  import Vue from "vue"
  import moment from 'moment'           
  import {
    mapGetters
  } from 'vuex'
   import {
    db
  } from '../../../firebase'
  Vue.component(VTable.name, VTable)
  Vue.component(VPagination.name, VPagination)
  import RunnersByRaceService from '../../../services/runnersByRace'
  import RunnersService from '../../../services/runner'
  import RaceQuestionService from '../../../services/raceQuestion'
  import TagService from '../../../services/tag'
  import PromoCodeService from '../../../services/promoCode'


  export default {
    name: 'runner-data',
    props: ['runnerId', 'raceId'],
    computed: {
      ...mapGetters({
        race: 'Race/race'
      })
    },
    data: () => ({
      //options: [{ "id": 1, "text": "Hello" }, { "id": 2, "text": "World" }, { "id": 3, "text": "opt 3" }, { "id": 4, "text": "opt 4" }, { "id": 5, "text": "opt 5" }, { "id": 6, "text": "opt 6" }, { "id": 7, "text": "opt 7" }, { "id": 8, "text": "opt 8" }],
      options: [],
      selected: [],
      runner: {},
      runnerData:{},
      runnerLoading: true,
      isLoading: true,
      raceData :{},
      showModal: false,
      showAddRaceModal:false,
      showAssignPromoCodeModal: false,
      showEmailModal: false,
      profileTexts:'',
      best: {},
      currentYearbest:{},
      AllTimebest:{},
      pastRaces: [],
      totalpastRuns:[],
      pageIndex:0,
      pageTotal:100,
      pageSize:30,
      memo:'',
      cmmemo:'',
      profileTextEdit:'',
      editingMemo: false,
      editingCMMemo: false,
      editingCatchPhrase: false,
      editingProfileText: false,
      Gold:0,
      Silver:0,
      Bronze:0,
      currentYear:'',
      createdOn:null,
      promoCodeList:[],
      selectedPromoTags:[],
      runmetrixData:{},
      tableConfig: {
                    multipleSort: false,
                    tableData: [],
                   columns:  [
                              {field: "finishDate", title:'Date',  width: 94, titleAlign: 'center',columnAlign:'center',isFrozen: true},
                              {field: "duration", title:'Duration',  width: 82, titleAlign: 'center',columnAlign:'center',isFrozen: true},
                              {field: "id", title:'Run ID',  width: 92, titleAlign: 'center',columnAlign:'center',componentName:'runner-past-run-id-link',isFrozen: true},
                              {field: "", title:'Map', width: 76, titleAlign: 'center',columnAlign:'center',componentName:'map-route',isFrozen: true},
                              {field: "name", title:'Name', width: 260, titleAlign: 'center',columnAlign:'center',isFrozen: true,
                              formatter: function (rowData,rowIndex,pagingIndex,field) {
                                return '<a class="race_name" href="#/admin/past-run/detail/'+rowData.id+'" target="_blank"><div>'+rowData.name+'</div></a>'
                                }
                              },    
                              {field: "trainer", title:'Trainer Name', width: 126, titleAlign: 'center',columnAlign:'center',isFrozen: true},                          
                              {field: "distance",  title:'Distance', width: 100, titleAlign: 'center',columnAlign:'center',componentName:'edit-operation',isFrozen: true},
                              {field: "place", title:'Position', width: 106, titleAlign: 'center',columnAlign:'center',isFrozen: true},
                              {field: 'custome-adv',width: 122, title: 'Operation', titleAlign: 'center',columnAlign:'center',
                                 componentName:'race-operation',isFrozen: true},
                              {field: "question", title:'Question', width: 240, titleAlign: 'center',columnAlign:'center',isResize:true},
                              {field: "answer", title:'Answer',  width: 420, titleAlign: 'left',columnAlign:'left',isResize:true, formatter: function (rowData,rowIndex,pagingIndex,field) {
                                return '<div class="rd-answer-cell">'+rowData.answer+'<br/><hr class="rd-answer-divider"><span class="rd-post-answer">'+rowData.postanswer+'</span></div>'
                                }
                              }, 
                              // {field: "answer", title:'Answer',  width: 500, titleAlign: 'left',columnAlign:'left', formatter: function (rowData,rowIndex,pagingIndex,field) {
                              //   return '<div style = "width:493px">'+rowData.answer+'<br/><hr style ="margin-bottom: 4px;margin-top: 4px;"><span style = "color:red">'+rowData.postanswer+'</span></div>'
                              //   }
                              // },                          


                            ]
                }
     
    }),
    methods: {
      initialize() {},
      async addRace(){
        // var raceInFb = await db.ref(`/${this.$fbDbEntry}/7445`).once('value')
        // raceInFb.val().raceResumeId 
        console.log("innnn---add race-")
        this.showAddRaceModal = true
      },
      onSavedRace(){
        this.showAddRaceModal = false
        this.pastRaces = []
        this.getPastRuns(this.pageIndex, this.pageSize) 
        this.getTableData() 
        this.getRunnerInfo()
      },
      onSavedPromoCode() {
         this.showAssignPromoCodeModal = false
         this.getRunnerInfo()
      },
      openAssignPromoCodeModal() {
          this.$modal.show('assignPromoCode')
      },
      deleteRace(){
        console.log("innnn---delete race-",)
      },
      openEmailEditModal(){
        this.showEmailModal = true
      },
      startEditMemo() {
        this.editingMemo = true
      },
      saveMemo() {
        var data = {
          runnerId: this.runner.runner.id,
          memo: {
            memo: this.memo
          }
        }
        RunnersByRaceService.saveMemo(data).then((response) => {
          this.editingMemo = false
        })
      },
      startEditCMMemo() {
        this.editingCMMemo = true
      },
      saveCMMemo() {
        var data = {
          runnerId: this.runner.runner.id,
          cmmemo: {
            cmmemo: this.cmmemo
          }
        }
        RunnersByRaceService.saveCmMemo(data).then((response) => {
          this.editingCMMemo = false
        })
      },
      onClosed(){
         this.showModal = false
         this.showEmailModal = false
         this.showAddRaceModal=false
         this.showAssignPromoCodeModal = false
      },
      onSaved(race){
         var foundIndex = this.pastRaces.findIndex(x => x.id == race.raceId)
         this.pastRaces[foundIndex].distance = (race.distance).toFixed(2) +' km'
         this.showModal = false
         
         this.getRunnerInfo()
         this.getTableData()
      },
      onSavedEmail(){
          this.showEmailModal = false
          this.getRunnerInfo()
      },
      customCompFunc(params){
        console.log("params----",params)
        // this.showModal = true
        // params.rowData['runnerId'] = this.$props.runnerId
        // this.raceData = params.rowData  

        if(params.columeName=="editDistance"){
          console.log("editDistance")
          this.showModal = true
          params.rowData['runnerId'] = this.$props.runnerId
          this.raceData = params.rowData  
        }else if(params.columeName=="deleteRace")    {
          console.log("deleteRace")
          this.$dialog.confirm("Are you sure you want to delete this run？", dialogOptions)
            .then((dialog) => {
                RunnersByRaceService.deleteRaceResume(params.rowData.runnerRaceResume.id).then((response) => {              
                  console.log("done",response)
                  this.$delete(this.tableConfig.tableData,params.index)
                })
            })
        }
        //else if(params.columeName=="addRace"){
        //   console.log("addRace")          
        //   params.rowData['runnerId'] = this.$props.runnerId
        //   this.raceData = params.rowData
        //   this.showAddRaceModal = true
          
        // }         
      },
      date(date) {
       return moment.utc(date).format('YYYY/MM/DD')
      },

      convertToLocal(date){
        var fdate = new Date(date)
        return fdate.toDateString()
      },
      getTableData(){
        console.log("this.pastRaces--->>>",this.pastRaces)
          this.tableConfig.tableData = this.pastRaces
          //this.pageTotal = this.totalpastRuns.length
      },
      pageChange(pageIndex){
      
            this.pageIndex = pageIndex-1
            this.pastRaces = []
            this.getTableData()
            this.getPastRuns(pageIndex, this.pageSize)
          
      },
      pageSizeChange(pageSize){

          this.pageIndex = 0
          this.pageSize = pageSize
          this.pastRaces = []
          this.getTableData()
          this.getPastRuns(this.pageIndex, pageSize)
          
      },
      unicodeToChar(text) {
        return text.replace(/\\u[\dA-F]{4}/gi, 
                function (match) {
                    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
                })
      },

      columnCellClass(rowIndex,columnName,rowData){
      
            if(columnName==='answer'){
                  return 'column-cell-class-name-test1'
            } 
            if(columnName==='distance'){
                  return 'column-cell-class-name-test2'
            }
      },

       getRunnerInfo() {
          this.runnerLoading = true
          RunnersByRaceService.getByRunnerId(this.$props.runnerId).then(async (response)=>{
          this.runner = response.data
          let user = await RunnersByRaceService.getUserInfo(response.data.runner.user.login)
          this.createdOn= moment(user.data.createdDate).format('L')

          this.runmetrixData = this.runner.runner.runmetrix

          console.log(" this.runner-********--", this.runmetrixData)
         
          console.log(" this.runner---", this.runner)
          this.getPromoCodeTags(this.runner)
         
          if(this.runner.totalSubscribedSessions != undefined){
            this.pageTotal = this.runner.totalSubscribedSessions
          }else{
            this.getSubscribedRaces()
          }
          // Set memo values from the new API response
          if(this.runner.runnerMemo && this.runner.runnerMemo.memo){
            this.memo = this.runner.runnerMemo.memo
          }

          if(this.runner.runnerCMMemo && this.runner.runnerCMMemo.cmmemo){
            this.cmmemo = this.runner.runnerCMMemo.cmmemo
          }
          this.profileTexts = this.unicodeToChar(response.data.runner.profileText)      
        }).finally(() => {
          this.runnerLoading = false
        })
        //Get All Time Best Records
        RunnersByRaceService.getBestDuration(this.$props.runnerId).then((response) => {
          this.best = response.data
          //Show only one best record in ALL TIME
          let obj = {}
             for (var key in this.best) {
              if (this.best.hasOwnProperty(key)) {
                  //  this.best[key]
                   let maxDistance
                  if(this.best[key].length>0){
                    maxDistance =  this.best[key].reduce((a, b) => a.distance > b.distance ? a : b)
                  }
                        obj[key] = maxDistance
              }
          }         
           this.AllTimebest = obj
        })

        //Get 2019 Best Records
        var d = new Date()
        this.currentYear = d.getFullYear()
        RunnersByRaceService.getCurrentYearBestDuration(this.$props.runnerId, this.currentYear).then((response) => {
          // let Yearbest = response.data
          // let obj = {}
          // for (var key in Yearbest) {
          //     if (Yearbest.hasOwnProperty(key)) {
          //          Yearbest[key]
          //          let maxDistance
          //           if(Yearbest[key].length>0){
          //               maxDistance =  Yearbest[key].reduce((a, b) => a.distance > b.distance ? a : b)
          //           }
                     
          //         obj[key] = maxDistance
          //     }
          // }
          this.currentYearbest = response.data
        })

        RunnersByRaceService.getRunnerMedals(this.$props.runnerId).then((response) => {
          response.data.forEach(medal => {
            if(medal.medal.badge === 'Gold'){
              this.Gold = medal.count
            }
            if(medal.medal.badge === 'Silver'){
              this.Silver = medal.count
            }
            if(medal.medal.badge === 'Bronze'){
              this.Bronze = medal.count
            }           
          })
        })
      },

      getPromoCodeTags(runner){
        //Get promocode tags list
        if(runner.runner && runner.runner.memberShip!=undefined &&
          runner.runner.memberShip.promoCodeList !=undefined ){
          this.promoCodeList = runner.runner.memberShip.promoCodeList //Get all promocodes of runner
          console.log("promoCodeList>>",this.promoCodeList)

          // let arr = this.promoCodeList.map(({ tags }) => tags)
          var promoCodeTags = this.promoCodeList.map(({ tags }) => tags[0] ) //Get tags of promocode
          promoCodeTags = promoCodeTags.filter((tags)=>tags != undefined) //remove undefined tags
          this.selectedPromoTags = promoCodeTags.map(a => a.id) //Get tag ids
        }
      },

      getPastRuns(index, pageSize){
          var data = { page: index-1, runnerId: this.$props.runnerId, size: pageSize ,sort:"finishDate,desc"} 
          this.isLoading = true
          RunnersByRaceService.getRunnerPastRuns(data).then((response)=>{
          this.isLoading = false
          //console.log("response--",response.data)
               response.data.forEach(race => {

                 //Position
                 var pos = race.runnerRaceResume.runnerPlace
                 var people = race.runnerRaceResume.raceResume.totalAmountRunners
                 var place =pos +'位'+ people + '人中'
                 race['place'] = place
                 //Date
                  // var fdate = new Date(race.runnerRaceResume.raceResume.finishDate)
                   var fdate = new Date(race.runnerRaceResume.finishDate)
                      fdate = fdate.toDateString()
                  race['finishDate'] = this.date(fdate)
                 //Duration
                 race['duration'] = race.runnerRaceResume.raceResume.race.duration+'分'
                  //Id
                 race['id'] = race.runnerRaceResume.raceResume.race.id
                 //Run ID
                 race['runId'] = race.runnerRaceResume.id
                 //Name
                 race['trainer'] = race.runnerRaceResume.raceResume.race.trainer.firstName 
                 race['name'] = race.runnerRaceResume.raceResume.race.name
                 //Distance
                 race['distance'] = race.runnerRaceResume.distanceLabel
                 //Question
                 race['question'] = race.questionAnswer?race.questionAnswer.raceQuestion.question:'-'
                 //PreAnswer
                 race['answer'] = race.questionAnswer?race.questionAnswer.answer:'-'

                 //PostAnswer
                RaceQuestionService.getRunnerPostAnswer(race.runnerRaceResume.raceResume.race.id,this.$props.runnerId)
                .then((response)=>{        
                  var postAnswer = response.body?response.body.answer:'-'
                    if(postAnswer != undefined){
                      race['postanswer'] = postAnswer
                    }           
                })
                 race['postanswer'] = '-'
                this.pastRaces.push(race)
               })
          })
      },
      getAllTags(){
         TagService.getAllTags().then((response)=>{
            this.options = response.data     
            this.getRunnerTags()
         })
      },

      getRunnerTags(){        
        TagService.getRunnerTags(this.$props.runnerId).then((response)=>{
          //console.log("this.options---",this.options.map(a => a.id))
            this.selected = response.data.map(a => a.id)
         })
      },
      getSubscribedRaces(){
        RunnersByRaceService.getSubscribedRaces(this.$props.runnerId).then((response)=>{
          this.totalpastRuns = response.data   
          this.pageTotal = this.totalpastRuns.length
        })
      },
      startEditCatchPhrase() {
        this.editingCatchPhrase = true
      },
      saveCatchPhrase() {
        RunnersService.update(this.runner.runner).then((response) => {
          console.log("Update CatchPhrase>", response.data)
          this.editingCatchPhrase = false
        })
      },
      startEditProfileText() {
        this.editingProfileText = true
        this.profileTextEdit = this.profileTexts || ''
      },
      saveProfileText() {
        // Update the runner's profile text
        this.runner.runner.profileText = this.profileTextEdit
        RunnersService.update(this.runner.runner).then((response) => {
          console.log("Update Profile Text>", response.data)
          this.profileTexts = this.profileTextEdit
          this.editingProfileText = false
        })
      },
      getRunnersData(id){
          let route = this.$router.resolve({
                    // path: '/runner/:runnerId?runmetrix == 1',
                    name: 'runner-data',
                    params: {
                          runnerId: this.$props.runnerId
                    },           
                    query: {
                      runmetrix:1
                    }                      
          })
          console.log(">>>>>>",route)
          window.open(route.href, '_blank')
      },
    },
    created() {
      this.getRunnerInfo()  
      this.getAllTags()
      //this.getRunnerTags()
     
      // RunnersByRaceService.getSubscribedRaces(this.$props.runnerId).then((response)=>{
      //   this.totalpastRuns = response.data   
      //   this.getPastRuns(this.pageIndex, this.pageSize) 
      //   this.getTableData()     
      // })

      this.getPastRuns(this.pageIndex, this.pageSize) 
      this.getTableData()    

    },
    components: {
      Container,
      EditDistance,
      EditEmail,
      AddRaceData,
      AssignPromoCode,
      Selectize,
      RunMetrix
    }
  }

  Vue.component('edit-operation',{
        template:`
        <span class="rd-distance-cell">
             <span>{{rowData.distance}}</span>
             <button class="rd-table-action-btn" @click.stop.prevent="openEditModal()">Edit</button>
        </span>
           `,
        props:{
            rowData:{
                type:Object
            },
            field:{
                type:String
            },
            index:{
                type:Number
            }
        },
        methods:{
          openEditModal(){
             let params = {columeName:"editDistance",rowData:this.rowData}
              this.$emit('on-custom-comp',params)
          }
        },
        created() { 
        //   this.id =   this.rowData.id
        }
  })
Vue.component('map-route',{
        template:`
        <span class="rd-map-cell">
          <button class="rd-table-action-btn" @click.stop.prevent="openEditModal()">Map</button>
        </span>
           `,
        props:{
            rowData:{
                type:Object
            },
            field:{
                type:String
            },
            index:{
                type:Number
            }
        },
        methods:{
          openEditModal(){
              let route = this.$router.resolve({
                    name: 'google-map',
                    params: {
                          raceId: this.rowData.id,
                          runnerId: this.rowData.runnerRaceResume.runner.id
                        }
                    })
              window.open(route.href, '_blank')
          }
        },
        created() { 
        //   this.id =   this.rowData.id
        }
  })

Vue.component('runner-past-run-id-link',{
        template:`
        <span>
          <a href="#" class="rd-run-id-link" @click.stop.prevent="openPastRun()">{{rowData.id}}</a>
        </span>
           `,
        props:{
            rowData:{
                type:Object
            },
            field:{
                type:String
            },
            index:{
                type:Number
            }
        },
        methods:{
          openPastRun(){
              const raceId = this.rowData.id
              const url = `${window.location.origin}/#/admin/past-run/detail/${raceId}`
              window.open(url, '_blank')
          }
        }
  })








        
  Vue.component('race-operation',{
        template:` <div class="rd-race-operation">
       <button class="rd-delete-run-btn" type="button" @click.stop.prevent="deleteRace()" title="Delete run">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14"/>
          </svg>
          Delete
        </button>
       </div>
      `,
        data: () => ({
          path: ''
        }),
        props:{
            rowData:{
                type:Object
            },
            field:{
                type:String
            },
            index:{
                type:Number
            }
        },
        methods:{
          deleteRace(){
            console.log("innnnnnnnnn",this.rowData.runnerRaceResume.id)
             let params = {columeName:"deleteRace",rowData:this.rowData,index:this.index}
              this.$emit('on-custom-comp',params)
          },
           addRace(){
            console.log("in addRace",this.rowData.runnerRaceResume.id)
             let params = {columeName:"addRace",rowData:this.rowData,index:this.index}
              this.$emit('on-custom-comp',params)
          }
        },
        created () {
          //this.mountPath()
        },

    })
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.rd-page {
  font-family: 'Inter', sans-serif;
  color: #1a1a2e;
}

.rd-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.rd-dashboard {
  display: grid;
  grid-template-columns: minmax(240px, 0.9fr) minmax(360px, 1.35fr) minmax(360px, 1.2fr);
  gap: 1.25rem;
  align-items: start;
}

.rd-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.rd-card-header,
.rd-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rd-card-header {
  margin-bottom: 1.25rem;
}

.rd-card-header--split {
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.rd-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
}

.rd-card-title {
  font-size: 16px;
  line-height: 1.15;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.rd-card-sub {
  font-size: 12px;
  color: #8a8a9a;
  margin: 4px 0 0;
}

.rd-runmetrix-link,
.rd-primary-btn,
.rd-ghost-btn,
.rd-table-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}

.rd-runmetrix-link,
.rd-primary-btn {
  background: #5b4fcf;
  color: #fff;
  border: none;
  height: 36px;
  padding: 0 18px;
}

.rd-runmetrix-link:hover,
.rd-primary-btn:hover {
  background: #4338ca;
  color: #fff;
}

.rd-ghost-btn {
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  height: 33px;
  padding: 0 14px;
}

.rd-ghost-btn:hover {
  background: #f0effe;
}

.rd-profile-top {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 1.25rem;
}

.rd-profile-image-container,
.rd-profile-loading {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 78px;
}

.rd-profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.rd-profile-placeholder {
  width: 100%;
  height: 100%;
  background: #fafafa;
  border: 1px solid #e8e8ed;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rd-profile-icon {
  width: 34px;
  height: 34px;
  color: #8a8a9a;
}

.rd-runner-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
}

.rd-runner-id {
  font-size: 12px;
  color: #8a8a9a;
  margin-top: 2px;
}

.rd-confidential {
  border: 1px solid #ffd6d6;
  border-radius: 10px;
  background: #fff8f8;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 1.2rem;
}

.rd-confidential-label {
  color: #c0392b;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rd-private-row,
.rd-detail-item,
.rd-activity-grid div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.rd-private-row span,
.rd-detail-item span,
.rd-activity-grid span,
.rd-block-label,
.rd-tag-label {
  font-size: 12px;
  color: #8a8a9a;
  font-weight: 600;
}

.rd-private-row strong,
.rd-detail-item strong,
.rd-activity-grid strong {
  font-size: 13px;
  color: #1a1a2e;
  font-weight: 700;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: right;
}

.rd-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.rd-detail-item {
  padding: 0.75rem;
  border: 1px solid #f0f0f5;
  border-radius: 9px;
  background: #fafafa;
}

.rd-detail-item--wide {
  grid-column: 1 / -1;
}

.rd-link {
  color: #5b4fcf;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
  text-align: right;
}

.rd-medal-row,
.rd-activity-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 1rem;
}

.rd-activity-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.rd-medal-pill,
.rd-activity-grid div {
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  padding: 0.55rem 0.7rem;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.rd-activity-grid div {
  border-radius: 9px;
  background: #fafafa;
  border: 1px solid #f0f0f5;
}

.rd-edit-block {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.rd-field-display,
.rd-field-edit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.rd-field-value {
  flex: 1;
  min-height: 34px;
  border: 1px solid #f0f0f5;
  border-radius: 9px;
  background: #fafafa;
  padding: 0.65rem 0.75rem;
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.rd-textarea {
  width: 100%;
  min-height: 92px;
  border: 1px solid #e8e8ed;
  border-radius: 9px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
  padding: 0.75rem;
}

.rd-textarea:focus {
  outline: none;
  border-color: #5b4fcf;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.12);
}

.rd-textarea--small {
  min-height: 74px;
}

.rd-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e8e8ed;
  background: #fff;
  color: #657084;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 30px;
}

.rd-icon-btn svg {
  width: 15px;
  height: 15px;
}

.rd-icon-btn:hover,
.rd-icon-btn--save {
  color: #5b4fcf;
  background: #f0effe;
  border-color: #d8d4fb;
}

.rd-best-table {
  display: flex;
  flex-direction: column;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  overflow: hidden;
}

.rd-best-head,
.rd-best-row {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) minmax(0, 1fr);
}

.rd-best-head {
  background: #fafafa;
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 700;
}

.rd-best-head span,
.rd-best-duration,
.rd-best-cell {
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f5;
}

.rd-best-row:last-child .rd-best-duration,
.rd-best-row:last-child .rd-best-cell {
  border-bottom: none;
}

.rd-best-duration {
  color: #5b4fcf;
  font-size: 13px;
  font-weight: 800;
  background: #fbfbff;
}

.rd-best-cell {
  font-size: 12px;
  min-height: 62px;
}

.rd-best-cell--highlight {
  background: #f5f4ff;
}

.rd-record-line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  line-height: 1.6;
}

.rd-record-line strong {
  color: #1a1a2e;
}

.rd-muted {
  color: #b1b1bf;
}

.rd-notes-grid,
.rd-tag-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.rd-tag-grid {
  margin-top: 1rem;
}

.rd-tag-field {
  min-width: 0;
}

.rd-tag-label {
  margin: 0 0 0.45rem;
}

.runner-tags .selectize-control,
.promocode-tags .selectize-control {
  width: 100%;
  margin-bottom: 0;
}

.runner-tags .selectize-control.multi .selectize-input,
.promocode-tags .selectize-control.multi .selectize-input {
  min-height: 38px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  box-shadow: none;
  padding: 6px 8px;
}

.runner-tags .selectize-control.multi .selectize-input > div,
.promocode-tags .selectize-control.multi .selectize-input > div {
  background: #f0effe;
  border: 1px solid #d8d4fb;
  color: #413b94;
  border-radius: 999px;
  margin: 0 6px 4px 0;
  padding: 2px 8px;
}

.rd-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.rd-table-wrap {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  overflow: auto;
  background: #fff;
  box-shadow: inset 0 0 0 1px #f0f0f5;
}

.rd-table-wrap .v-table-class {
  border: 0 !important;
  color: #1a1a2e;
}

.rd-table-wrap .v-table-rightview,
.rd-table-wrap .v-table-leftview {
  font-size: 12px;
  border-color: #f0f0f5 !important;
}

.rd-table-wrap .v-table-title-cell {
  background: #5b4fcf !important;
  color: #fff !important;
  font-weight: 700;
  height: 40px !important;
  line-height: 40px !important;
  padding: 0 12px !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  white-space: nowrap;
}

.rd-table-wrap .v-table-body-cell {
  color: #1a1a2e !important;
  min-height: 58px;
  height: 58px !important;
  padding: 9px 10px !important;
  border-color: #f0f0f5 !important;
  line-height: 1.4 !important;
  vertical-align: middle !important;
  word-break: normal;
  overflow-wrap: anywhere;
}

.rd-table-wrap .v-table-row:hover .v-table-body-cell {
  background: #fafafa !important;
}

.rd-table-wrap .v-table-body-row:nth-child(even) .v-table-body-cell {
  background: #fbfbfd;
}

.column-cell-class-name-test1 .v-table-body-cell {
  line-height: 1.35 !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  vertical-align: middle;
  display: table-cell;
  height: 58px !important;
}

.column-cell-class-name-test2 .v-table-body-cell {
  line-height: 1.3 !important;
}

.race_name {
  color: #1a1a2e;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  line-height: 1.35;
}

.race_name:hover,
.rd-run-id-link:hover {
  color: #5b4fcf;
}

.rd-run-id-link {
  color: #423799;
  cursor: pointer;
  font-weight: 700;
  text-decoration: underline;
}

.rd-distance-cell,
.rd-map-cell,
.rd-race-operation {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
}

.rd-distance-cell {
  flex-direction: column;
  gap: 5px;
}

.rd-distance-cell > span {
  min-height: 16px;
  line-height: 1.2;
}

.rd-table-action-btn {
  min-width: 58px;
  height: 30px;
  border: 1px solid transparent;
  background: #5b4fcf;
  color: #fff;
  padding: 0 12px;
  font-size: 12px;
  border-radius: 8px;
  font-weight: 700;
  line-height: 1;
}

.rd-table-action-btn:hover {
  background: #4338ca;
}

.rd-delete-run-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 30px;
  border: none;
  background: transparent;
  color: #1a1a2e;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.rd-delete-run-btn svg {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e74c3c;
  color: #fff;
  padding: 7px;
  flex: 0 0 28px;
}

.rd-answer-cell {
  width: 100%;
  min-height: 32px;
  line-height: 1.45;
}

.rd-answer-divider {
  border: none;
  border-top: 1px solid #d8d8e2;
  margin: 4px 0;
}

.rd-post-answer {
  color: #c0392b;
}

.rd-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  font-weight: 700;
}

.rd-pagination .v-pagination {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.rd-pagination .page-total,
.rd-pagination .page-size-select,
.rd-pagination .jump-page {
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 700;
}

.rd-table-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rd-table-sk-row {
  height: 42px;
}

.rd-sk-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.8rem;
}

.rd-sk-label {
  width: 110px;
  height: 16px;
}

.rd-sk-value {
  flex: 1;
  height: 16px;
}

.rd-sk-line {
  height: 14px;
  width: 68%;
  margin-top: 0.75rem;
}

.rd-sk-line--wide {
  width: 88%;
}

.rd-best-skeleton {
  height: 54px;
  margin-top: 0.65rem;
}

@media (max-width: 1320px) {
  .rd-dashboard {
    grid-template-columns: minmax(260px, 0.85fr) minmax(360px, 1.15fr);
  }

  .rd-best-card {
    grid-column: 1 / -1;
  }
}

@media (max-width: 860px) {
  .pg-title-row,
  .rd-card-header--split {
    align-items: flex-start;
    flex-direction: column;
  }

  .rd-dashboard,
  .rd-notes-grid,
  .rd-tag-grid,
  .rd-detail-grid {
    grid-template-columns: 1fr;
  }

  .rd-detail-item--wide {
    grid-column: auto;
  }

  .rd-activity-grid,
  .rd-medal-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rd-best-head,
  .rd-best-row {
    grid-template-columns: 76px minmax(0, 1fr) minmax(0, 1fr);
  }

  .rd-card {
    padding: 1.25rem;
  }

  .rd-actions {
    justify-content: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// RunnerData.vue?0b314ad4